import { eventTypeTypeValues } from "@ac-expo/api-types";
import { isBefore } from "date-fns";
import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";
import { fileSchema } from "../files/files.schema";
import { invalidPlatformUrl, invalidUrl, validatePlatformUrl } from "../structure";
import { CustomEventAppearanceSchema } from "../utils";
import { defaultPlaceValues, placeSchema } from "./event-place.schema";
import {
  upsertEventDateRangeSchema,
  upsertEventExceptionalClosingDateSchema,
} from "./upsert-event-dates.schema";
import { acExpoInterlocutorSchema } from "./ac-expo-interlocutor.schema";

export const createDatesSchema = () => {
  return z.object({
    dates: z.object({
      from: z.date().refine((date) => !isNaN(date.getTime()), {
        message: "Date de début invalide",
      }),
      to: z
        .date()
        .refine((date) => !isNaN(date.getTime()), {
          message: "Date de fin invalide",
        })
        .optional(),
    }),
  });
};
export type DatesSchema = ReturnType<typeof createDatesSchema>;
export type DatesForm = z.infer<DatesSchema>;

export const createUpdateEventSchema = () => {
  return z
    .object({
      id: z.string().min(1),
      image: fileSchema,
      pictures: z.array(
        z.object({
          legend: z.string().nullable(),
          order: z.number(),
          file: fileSchema,
        }),
      ),
      isAvailable: z.boolean(),
      availableStatus: z.enum(["draft", "online"]),
      openTicketingWhenOnline: z.boolean(),
      activateBadgesWhenOnline: z.boolean(),
      setExhibitorsOnlineWhenOnline: z.boolean(),
      setProgramOnlineWhenOnline: z.boolean(),
      isPrivate: z.boolean(),
      privateStatus: z.enum(["public", "private"]),
      isFromAi: z.boolean().optional(),
      shouldUpdateStatus: z.boolean().optional(),
      name: z.string().min(1, "Le nom de l'événement est requis"),
      type: z.enum([...eventTypeTypeValues]),
      description: z
        .string()
        .max(5000, "Le texte de la description ne doit pas dépasser 5000 caractères")
        .min(1, "La description est requise"),
      organiserName: z.string().min(1, "Le nom de l'organisateur est requis"),
      organiserEmail: z.string().email("Email invalide"),
      organiserPhone: z
        .string()
        .refine((phone) => !phone || isValidPhoneNumber(phone, "FR"), {
          message: "Téléphone invalide",
        })
        .optional(),
      organiserPhone2: z
        .string()
        .refine((phone) => !phone || isValidPhoneNumber(phone, "FR"), {
          message: "Téléphone invalide",
        })
        .optional(),
      place: placeSchema.superRefine((place, ctx) => {
        if (!place.address.country) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["address", "country"],
          });
        }
      }),
      entry: z.enum(["free", "paid"], {
        message: "Le type d'entrée est requis",
      }),
      venue: z
        .object({
          indoor: z.boolean(),
          outdoor: z.boolean(),
        })
        .refine((data) => data.indoor || data.outdoor, {
          message: "Le type de lieu est requis",
        }),
      dates: z.object({
        schedules: z.array(upsertEventDateRangeSchema).superRefine((schedules, ctx) => {
          let hasMissingDates = false;
          schedules.forEach((opening, index) => {
            if (!opening.dates || !opening.dates.from || !opening.dates.to) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Veuillez renseigner les dates d'ouverture",
                path: [index, "dates"],
              });
              hasMissingDates = true;
            }
          });
          if (hasMissingDates) return;
          const datesSorted = (schedules.map((s) => s.dates) as { from: Date; to: Date }[]).sort(
            (a, b) => (isBefore(a.from, b.from) ? -1 : 1),
          );

          for (let i = 1; i < datesSorted.length; i++) {
            const prevEnd = datesSorted[i - 1].to;
            const currentStart = datesSorted[i].from;
            // Check if the current start date is before or on the previous end date
            if (currentStart <= prevEnd) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Les plages de dates ne doivent pas se chevaucher",
                path: [i, "dates"],
              });
            }
          }
        }),
        exceptionalClosingDates: z.array(upsertEventExceptionalClosingDateSchema),
        isContinuous: z.boolean(),
      }),
      isGlobalTime: z.boolean(),
      websiteUrl: z.union([z.string().url(), z.string().length(0)]).optional(),
      linkedinUrl: z
        .string()
        .url(invalidUrl)
        .refine(validatePlatformUrl(["linkedin.com"]), {
          message: invalidPlatformUrl("LinkedIn"),
        })
        .or(z.literal(""))
        .nullish(),
      facebookUrl: z
        .string()
        .url(invalidUrl)
        .refine(validatePlatformUrl(["facebook.com"]), {
          message: invalidPlatformUrl("Facebook"),
        })
        .or(z.literal(""))
        .nullish(),
      twitterUrl: z
        .string()
        .url(invalidUrl)
        .refine(validatePlatformUrl(["twitter.com", "x.com"]), {
          message: invalidPlatformUrl("X (Twitter)"),
        })
        .or(z.literal(""))
        .nullish(),
      instagramUrl: z
        .string()
        .url(invalidUrl)
        .refine(validatePlatformUrl(["instagram.com"]), {
          message: invalidPlatformUrl("Instagram"),
        })
        .or(z.literal(""))
        .nullish(),
      youtubeUrl: z
        .string()
        .url(invalidUrl)
        .refine(validatePlatformUrl(["youtube.com"]), {
          message: invalidPlatformUrl("YouTube"),
        })
        .or(z.literal(""))
        .nullish(),
      customEventAppearance: CustomEventAppearanceSchema.nullable(),
      additionalInformation: z
        .object({
          exhibitorCount: z.coerce.number(),
          visitorCount: z.coerce.number(),
          animationCount: z.coerce.number(),
          ticketingAmount: z.coerce.number(),
          hasToBeRecontacted: z.boolean(),
          additionalInterlocutorInformations:
            acExpoInterlocutorSchema.optional(),
        })
        .optional(),
    })
    .refine(({ image, availableStatus }) => availableStatus === "draft" || image.key !== "", {
      path: ["image"],
      message: "Veuillez sélectionner une image pour mettre l'événement en ligne",
    })
    .superRefine((data, ctx) => {
      if (data.isFromAi && !data.organiserEmail) {
        ctx.addIssue({
          path: ["organiserEmail"],
          code: z.ZodIssueCode.custom,
          message: "L'adresse email de l'organisateur est obligatoire",
        });
      }
    });
};

export type UpdateEventSchema = ReturnType<typeof createUpdateEventSchema>;
export type UpdateEventForm = z.infer<UpdateEventSchema>;

export const defaultUpdateEventValues = {
  id: "",
  image: {
    key: "",
    name: "",
    type: "media" as const,
  },
  isAvailable: false,
  availableStatus: "draft" as const,
  isFromAi: false,
  shouldUpdateStatus: false,
  openTicketingWhenOnline: false,
  activateBadgesWhenOnline: false,
  setExhibitorsOnlineWhenOnline: false,
  setProgramOnlineWhenOnline: false,
  isPrivate: false,
  privateStatus: "public" as const,
  name: "",
  type: "other" as const,
  description: "",
  organiserName: "",
  organiserEmail: "",
  organiserPhone: "",
  organiserPhone2: "",
  place: defaultPlaceValues,
  entry: "free" as const,
  venue: {
    indoor: false,
    outdoor: false,
  },
  dates: { schedules: [], exceptionalClosingDates: [], isContinuous: false },
  isGlobalTime: true,
  pictures: [],
  country: "",
  websiteUrl: "",
  linkedinUrl: "",
  facebookUrl: "",
  twitterUrl: "",
  instagramUrl: "",
  youtubeUrl: "",
};
